(() => {
  const html = document.documentElement, motion = html.classList.contains('lx-motion'), cfg = window.LX || {};
  const design = !!(window.Shopify && Shopify.designMode);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const hasGsap = () => motion && window.gsap && window.ScrollTrigger;
  if (hasGsap()) { gsap.registerPlugin(ScrollTrigger); ScrollTrigger.config({ ignoreMobileResize: true }); }
  const webgl = () => { try { const c = document.createElement('canvas'); return !!(c.getContext('webgl2') || c.getContext('webgl')); } catch (e) { return false; } };
  const conn = navigator.connection || {};
  const can3d = motion && !!cfg.engine && !conn.saveData && (navigator.deviceMemory || 8) > 2 && webgl();
  if (!can3d) html.classList.add('lx-no3d');
  const isM = () => innerWidth < 750;

  let enginePromise;
  const engine = () => enginePromise || (enginePromise = new Promise((res, rej) => {
    const start = () => { const s = document.createElement('script'); s.src = cfg.engine; s.async = true; s.onload = () => res(window.LXEngine); s.onerror = rej; document.head.append(s); };
    // Waits for first paint so the 3D bundle never competes with page content.
    // Starts shortly after first paint, without waiting for slower third-party scripts.
    setTimeout(() => ('requestIdleCallback' in window ? requestIdleCallback(start, { timeout: 600 }) : start()), isM() ? 900 : 400);
  }));
  const fail = e => { html.classList.add('lx-no3d'); console.warn('3D unavailable, showing images', e); };

  /* Smooth scroll: desktop only, off in the editor */
  let lenis = null;
  // Downloaded only on desktop pointers; phones keep native scrolling.
  if (hasGsap() && cfg.lenis && !design && innerWidth >= 990 && matchMedia('(hover:hover) and (pointer:fine)').matches) {
    const s = document.createElement('script'); s.src = cfg.lenis; s.async = true;
    s.onload = () => {
      lenis = new Lenis({ lerp: 0.11, wheelMultiplier: 1 });
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add(t => lenis.raf(t * 1000)); gsap.ticker.lagSmoothing(0);
      new MutationObserver(() => { const lock = !!document.querySelector('dialog[open]') || document.body.classList.contains('lx-lock'); lock ? lenis.stop() : lenis.start(); })
        .observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open', 'class'] });
    };
    document.head.append(s);
  }

  /* One bottle travels through the whole homepage */
  const POSES = { hero: { ry: -0.42, rx: 0.04, cap: 0, s: 1 }, front: { ry: 0, rx: 0, cap: 0, s: 1.08 }, side: { ry: 0.95, rx: 0.06, cap: 0, s: 1.02, rz: 0.16 }, open: { ry: -0.75, rx: 0.08, cap: 1, s: 0.84, mist: 1 }, turn: { ry: 6.28, rx: 0, cap: 0, s: 1.08 } };
  const place = {
    story: () => isM() ? { x: 0, y: 0, s: 0.8, ry: 0.5, rx: 0, cap: 0, o: 0.2 } : { x: -2.5, y: -0.15, s: 0.88, ry: 0.55, rx: 0.02, cap: 0, o: 1 },
    frag: () => isM() ? { x: 0, y: -0.44, s: 0.667, ry: 6.28, rx: 0, cap: 0, o: 1 } : { x: -1.85, y: -0.12, s: 0.95, ry: 6.28, rx: 0, cap: 0, o: 1 },
    statement: () => isM() ? { x: 0, y: 0, s: 0.72, ry: 12.56, rx: 0.05, cap: 1, o: 0.28 } : { x: 0, y: -0.1, s: 1.2, ry: 12.56, rx: 0.05, cap: 1, o: 0.42 }
  };

  function world() {
    const el = document.querySelector('[data-lx-world]'), hero = document.querySelector('[data-lx-hero]');
    if (!el || !hero) return;
    const chapters = $$('[data-lx-chapter]', hero), setActive = i => chapters.forEach((c, n) => c.classList.toggle('is-active', n === i));
    setActive(0); hero.classList.add('is-ready');
    const heroBase = () => isM() ? { x: 0, y: -0.36, m: 0.68 } : { x: 1.8, y: -0.1, m: 0.92 };
    // Phones: fit the bottle between the header and this chapter's text, whatever the screen and browser bars.
    const fitPhone = (i, p, base) => {
      const c = chapters[i], H = el.querySelector('canvas').clientHeight, header = document.querySelector('.lx-header');
      if (!H || !header) return { y: base.y, s: base.s };
      return fitBetween(header.offsetHeight + (i ? 0 : 40) + 8, Math.min(H, c.offsetTop + (i ? 0 : top(hero))) - 14, H, p.cap, base);
    };
    const fitBetween = (head, foot, H, cap, base) => {
      const u = H / 4.93, up = 1.76 + cap * 0.62, down = -1.85;
      const room = Math.max(120, foot - head), s = Math.min(base.s, room / (u * (up - down)) * 0.96), mid = head + room / 2;
      return { y: (H / 2 - mid) / u - s * (up + down) / 2, s, top: mid - s * u * (up - down) / 2, bottom: mid + s * u * (up - down) / 2 };
    };
    // The explorer keeps its bottle clear of its heading and panel, and tells the ingredients where it sits.
    const fragPose = s => {
      const p = { ...place.frag(), rg: 0 }, st = s.querySelector('[data-lx-stage]'), h = s.querySelector('.lx-frag__head'), pn = s.querySelector('.lx-frag__tabs'), H = el.querySelector('canvas').clientHeight;
      if (!isM() || !st || !h || !pn || !H) return p;
      const t = st.getBoundingClientRect().top, f = fitBetween(h.getBoundingClientRect().bottom - t + 6, Math.min(H, pn.getBoundingClientRect().top - t) - 10, H, 0, { s: p.s });
      s.style.setProperty('--ft', f.top + 'px'); s.style.setProperty('--fb', f.bottom + 'px');
      return { ...p, y: f.y, s: f.s };
    };
    const chapterPose = i => { const p = POSES[chapters[i].dataset.pose] || POSES.hero, b = heroBase(), base = { y: b.y, s: p.s * b.m }, f = isM() ? fitPhone(i, p, base) : base; return { x: b.x, y: f.y, s: f.s, ry: p.ry, rx: p.rx, cap: p.cap, o: 1, rz: p.rz || 0, mist: p.mist || 0, rg: 1 }; };
    let scene = null, keys = [];
    const wrap = n => n.closest('.shopify-section') || n;
    const top = n => wrap(n).getBoundingClientRect().top + scrollY;
    const build = () => {
      const vh = innerHeight, K = [], hTop = top(hero), hEnd = Math.max(1, hero.offsetHeight - vh), n = chapters.length;
      chapters.forEach((c, i) => K.push({ y: hTop + hEnd * (n > 1 ? i / (n - 1) : 0), p: chapterPose(i) }));
      $$('[data-lx-b]').forEach(s => {
        const w = wrap(s), t = top(s), h = w.offsetHeight, b = s.dataset.lxB, last = K[K.length - 1].p;
        if (isM() && b !== 'frag') { const off = { ...last, o: 0 }; K.push({ y: t - vh * 0.85, p: off }, { y: t + h - vh * 0.15, p: off }); }
        else if (b === 'hide') { const off = { ...last, o: 0 }; K.push({ y: t - vh * 0.35, p: off }, { y: t + h - vh * 0.75, p: off }); }
        else if (b === 'frag') { const fp = fragPose(s); K.push({ y: t, p: fp }, { y: t + h - vh, p: fp }); }
        else K.push({ y: t + h / 2 - vh / 2, p: place[b]() });
      });
      keys = K.sort((a, b) => a.y - b.y).map(k => ({ y: k.y, p: { rz: 0, mist: 0, rg: 1, ...k.p } }));
    };
    const update = () => {
      if (!scene || !keys.length) return;
      const y = scrollY; let i = 0;
      while (i < keys.length - 1 && keys[i + 1].y <= y) i++;
      const a = keys[i], b = keys[Math.min(i + 1, keys.length - 1)];
      let f = b.y > a.y ? Math.min(1, Math.max(0, (y - a.y) / (b.y - a.y))) : 0; f = f * f * (3 - 2 * f);
      const out = {}; for (const k in a.p) out[k] = a.p[k] + (b.p[k] - a.p[k]) * f;
      scene.setPose(out);
      const wo = Math.max(0, Math.min(1, out.o)).toFixed(2); if (wo !== el._wo) { el._wo = wo; el.style.setProperty('--wo', wo); }
    };
    const canvas = el.querySelector('canvas');
    window.LXWorld = { get scene() { return scene; }, reset() { if (scene) scene.setFragrance(hero.dataset.label, hero.dataset.accent, hero.dataset.color, hero.dataset.face); el.style.setProperty('--accent', hero.dataset.accent); } };
    if (can3d) engine().then(E => {
      if (!E) throw new Error('no engine');
      build();
      scene = new E.BottleScene(canvas, { logo: cfg.logo, brand: cfg.brand, capFace: cfg.capFace, label: hero.dataset.label, face: hero.dataset.face, accent: hero.dataset.accent, color: hero.dataset.color, pose: { ...chapterPose(0), y: chapterPose(0).y - 0.18, s: chapterPose(0).s * 0.9, ry: chapterPose(0).ry + 0.35, rz: -0.22, cap: 2.2, rg: 0, o: 0 } });
      // Hero opening: the cap settles onto the bottle while the rings open out.
      scene.lerp = 0.035; setTimeout(() => { scene.lerp = 0; }, 1900);
      html.classList.add('lx-has3d'); update();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { build(); update(); });
      if (window.LXPending) window.LXPending();
    }).catch(fail);
    if (hasGsap()) {
      ScrollTrigger.create({ trigger: hero, start: 'top top', end: 'bottom bottom', onUpdate: t => { setActive(Math.min(chapters.length - 1, Math.round(t.progress * (chapters.length - 1)))); hero.style.setProperty('--p', t.progress.toFixed(3)); } });
      ScrollTrigger.addEventListener('refresh', () => { build(); update(); });
      ScrollTrigger.create({ start: 0, end: 'max', onUpdate: update });
    }
  }

  function fragrance(root) {
    const sec = root.querySelector('[data-lx-fragrance]'); if (!sec) return;
    const floats = $$('[data-lx-float]', sec), panels = $$('[data-lx-panel]', sec), tabs = $$('[role=tab]', sec), W = document.querySelector('[data-lx-world]');
    let cur = -1;
    const apply = () => { const P = panels[cur], s = window.LXWorld && LXWorld.scene; if (P && s) s.setFragrance(P.dataset.name, P.dataset.accent, P.dataset.color, P.dataset.face); };
    const go = i => {
      if (i === cur) return; cur = i; const P = panels[i]; if (!P) return;
      panels.forEach((x, n) => { x.classList.toggle('is-active', n === i); x.setAttribute('aria-hidden', n !== i); });
      floats.forEach((x, n) => x.classList.toggle('is-active', n === i));
      tabs.forEach((t, n) => { t.setAttribute('aria-selected', n === i); t.tabIndex = n === i ? 0 : -1; if (n === i && isM()) t.parentElement.scrollTo({ left: t.offsetLeft - 20, behavior: 'smooth' }); });
      sec.style.setProperty('--accent', P.dataset.accent); if (W) W.style.setProperty('--accent', P.dataset.accent);
      $$('[data-lx-fallback]', sec).forEach((im, n) => im.classList.toggle('is-active', n === i));
      const ghost = sec.querySelector('[data-lx-ghost]'); if (ghost) ghost.textContent = P.dataset.name;
      apply();
    };
    let st = null; go(0); window.LXPending = () => { if (st && st.isActive) apply(); };
    if (hasGsap() && panels.length > 1) st = ScrollTrigger.create({ trigger: sec, start: 'top top', end: 'bottom bottom', onUpdate: t => go(Math.min(panels.length - 1, Math.floor(t.progress * panels.length))), onEnter: apply, onEnterBack: apply, onLeaveBack: () => { cur = -1; go(0); if (window.LXWorld) LXWorld.reset(); } });
    tabs.forEach((t, i) => {
      t.addEventListener('click', () => { if (st) { const y = st.start + (st.end - st.start) * ((i + 0.5) / panels.length); lenis ? lenis.scrollTo(y) : scrollTo({ top: y, behavior: 'smooth' }); } go(i); });
      t.addEventListener('keydown', e => { const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0; if (d) { const n = tabs[(i + d + tabs.length) % tabs.length]; n.focus(); n.click(); } });
    });
  }

  function pdp(root) {
    const el = root.querySelector('[data-lx-pdp]'); if (!el || !can3d) return;
    const D = el.dataset, canvas = el.querySelector('canvas');
    // Tall gallery scrolls with the page, then rests so its end stays in view beside the details.
    const gal = el.closest('.presence-gallery');
    if (gal) { const fit = () => { gal.style.top = innerWidth >= 990 ? Math.min(96, innerHeight - gal.offsetHeight - 16) + 'px' : ''; }; fit(); addEventListener('resize', fit, { passive: true }); new ResizeObserver(fit).observe(gal); }
    el.style.setProperty('--accent', D.accent);
    const start = () => engine().then(E => {
      if (!E) throw new Error('no engine');
      const s = new E.BottleScene(canvas, { logo: cfg.logo, brand: cfg.brand, capFace: cfg.capFace, label: D.name, face: D.face, accent: D.accent, color: D.color, drag: true, pose: { x: 0, y: 0.32, s: 1.02, ry: -0.42, rx: 0.04 } });
      el.classList.add('is-3d');
      if (hasGsap() && !isM()) ScrollTrigger.create({ trigger: el, start: 'top top', end: '+=900', scrub: true, onUpdate: t => s.setPose({ ry: -0.42 + t.progress * 1.6, cap: t.progress > 0.25 ? Math.min(1, (t.progress - 0.25) * 2) : 0 }) });
    }).catch(fail);
    start();
  }

  function rail(root) {
    $$('[data-lx-rail]', root).forEach(sec => {
      const track = sec.querySelector('[data-lx-track]'), count = sec.querySelector('[data-lx-count]');
      const cards = [...track.children]; if (cards.length < 2) return;
      const prev = sec.querySelector('[data-lx-prev]'), next = sec.querySelector('[data-lx-next]');
      const auto = sec.dataset.auto === 'true' && motion;
      if (auto) {
        // Second copy makes the loop seamless; hidden from keyboards and screen readers.
        cards.forEach(c => { const k = c.cloneNode(true); k.setAttribute('aria-hidden', 'true'); k.inert = true; track.append(k); });
        track.classList.add('is-marquee');
      }
      const gap = () => cards[1].offsetLeft - cards[0].offsetLeft;
      const loop = () => gap() * cards.length;
      let pos = 0, hold = 0, over = false, seen = false, last = 0, raf = 0;
      const speed = (Number(sec.dataset.speed) || 30) * (isM() ? 0.65 : 1);
      const label = () => { if (count) count.textContent = String(Math.round(track.scrollLeft / gap()) % cards.length + 1).padStart(2, '0'); };
      const tick = t => {
        raf = 0; if (!auto || !seen || document.hidden) return;
        const dt = Math.min(0.05, (t - last) / 1000 || 0); last = t;
        if (!over && t > hold) { pos += speed * dt; if (pos >= loop()) pos -= loop(); track.scrollLeft = pos; } else pos = track.scrollLeft;
        raf = requestAnimationFrame(tick);
      };
      const run = () => { if (auto && !raf) { last = performance.now(); raf = requestAnimationFrame(tick); } };
      const step = d => {
        hold = performance.now() + 2200;
        if (auto) { if (d < 0 && track.scrollLeft < gap()) track.scrollLeft += loop(); if (d > 0 && track.scrollLeft >= loop()) track.scrollLeft -= loop(); }
        track.scrollBy({ left: d * gap(), behavior: motion ? 'smooth' : 'auto' });
      };
      prev.addEventListener('click', () => step(-1)); next.addEventListener('click', () => step(1));
      track.addEventListener('scroll', () => requestAnimationFrame(label), { passive: true });
      if (!auto) return;
      new IntersectionObserver(([e]) => { seen = e.isIntersecting; run(); }).observe(track);
      document.addEventListener('visibilitychange', run);
      sec.addEventListener('pointerenter', e => { if (e.pointerType === 'mouse') over = true; });
      sec.addEventListener('pointerleave', e => { if (e.pointerType === 'mouse') { over = false; pos = track.scrollLeft; } });
      sec.addEventListener('focusin', () => { over = true; }); sec.addEventListener('focusout', () => { over = false; });
      track.addEventListener('touchstart', () => { hold = Infinity; }, { passive: true });
      track.addEventListener('touchend', () => { hold = performance.now() + 2500; }, { passive: true });
    });
  }

  /* Hover image: fetched only on devices that can hover */
  function altImages(root) {
    if (!matchMedia('(hover:hover) and (pointer:fine)').matches) return;
    $$('[data-alt-src]', root).forEach(im => {
      const card = im.closest('.lx-card');
      card.addEventListener('pointerenter', () => { if (!im.src) { im.onload = () => card.classList.add('has-alt'); im.src = im.dataset.altSrc; } }, { once: true });
    });
  }

  /* Quick View loads only when opened */
  function quickView() {
    const d = document.getElementById('LxQuickView'); if (!d || d._on) return; d._on = 1;
    const body = d.querySelector('[data-qv-body]');
    document.addEventListener('click', async e => {
      const b = e.target.closest('[data-lx-quick]');
      if (e.target.closest('[data-lx-close]') || e.target === d) return void d.close();
      if (!b) return;
      body.innerHTML = '<div class="lx-qv-load" aria-busy="true"></div>'; d.showModal(); document.body.classList.add('lx-lock');
      try {
        const r = await fetch(b.dataset.lxQuick + '?section_id=lx-quick-view'); if (!r.ok) throw 0;
        const c = new DOMParser().parseFromString(await r.text(), 'text/html').querySelector('.lx-qv'); if (!c) throw 0;
        body.replaceChildren(c);
        const h = c.querySelector('h2'); h.tabIndex = -1; h.focus({ preventScroll: true });
      } catch (err) { location.href = b.dataset.lxQuick; }
    });
    d.addEventListener('close', () => document.body.classList.remove('lx-lock'));
    d.addEventListener('change', e => { if (e.target.matches('[data-qv-variant]')) { const p = d.querySelector('[data-qv-price]'); p.textContent = e.target.selectedOptions[0].dataset.p; } });
    d.addEventListener('submit', () => setTimeout(() => { if (d.open) d.close(); }, 700));
    document.addEventListener('change', e => { if (e.target.closest('[data-lx-sort]')) e.target.form.submit(); });
  }

  function words(root) {
    $$('[data-lx-words]', root).forEach(el => {
      if (!hasGsap() || isM() || el.dataset.split) return; el.dataset.split = 1;
      el.setAttribute('aria-label', el.textContent.trim());
      el.innerHTML = el.textContent.trim().split(/\s+/).map(w => `<span class="lx-word" aria-hidden="true">${w.replace(/&/g, '&amp;').replace(/</g, '&lt;')} </span>`).join('');
      gsap.fromTo(el.children, { opacity: 0.14 }, { opacity: 1, stagger: 0.08, ease: 'none', scrollTrigger: { trigger: el, start: 'top 82%', end: 'bottom 45%', scrub: 0.4 } });
    });
    $$('[data-lx-parallax]', root).forEach(el => {
      if (!hasGsap() || isM()) return;
      gsap.fromTo(el, { yPercent: -7 }, { yPercent: 7, ease: 'none', scrollTrigger: { trigger: el.parentElement, start: 'top bottom', end: 'bottom top', scrub: true } });
    });
  }

  const LEGACY = '.presence-information-hero,.presence-faq-group,.presence-information-index,.presence-policy-section,.presence-cart-item,.presence-cart-summary,.presence-cart-heading,.presence-page-title,.presence-about-content>*,.presence-search-hero,.presence-not-found>*,.presence-product-panel>*,.presence-product-aftercare>*,.presence-product-aftercare article,.presence-track-order-layout>*,.presence-gallery-main,.at-related-product,.presence-page-content>*';
  function reveal(root) {
    $$(LEGACY, root).forEach((e, i) => { if (!e.closest('.lx-no-reveal')) { e.setAttribute('data-lx-reveal', ''); } });
    const els = $$('[data-lx-reveal]', root);
    if (!motion) return els.forEach(e => e.classList.add('is-in'));
    const io = new IntersectionObserver(es => es.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } }), { rootMargin: '0px 0px -6% 0px' });
    els.forEach(e => io.observe(e));
  }

  function tilt(root) {
    if (!motion || !matchMedia('(hover:hover) and (pointer:fine)').matches) return;
    $$('[data-lx-tilt]', root).forEach(c => {
      c.addEventListener('pointermove', e => { const b = c.getBoundingClientRect(), x = (e.clientX - b.left) / b.width, y = (e.clientY - b.top) / b.height; c.style.setProperty('--rx', (y - 0.5) * -9 + 'deg'); c.style.setProperty('--ry', (x - 0.5) * 11 + 'deg'); c.style.setProperty('--mx', x * 100 + '%'); c.style.setProperty('--my', y * 100 + '%'); });
      c.addEventListener('pointerleave', () => { c.style.setProperty('--rx', '0deg'); c.style.setProperty('--ry', '0deg'); });
    });
  }

  function header() {
    const h = document.querySelector('[data-lx-header]'); if (!h) return;
    const onScroll = () => h.classList.toggle('is-stuck', scrollY > 24); onScroll(); addEventListener('scroll', onScroll, { passive: true });
    const btn = h.querySelector('[data-lx-menu-toggle]'), panel = h.querySelector('[data-lx-menu]'); if (!btn || !panel) return;
    const set = o => { btn.setAttribute('aria-expanded', o); panel.classList.toggle('is-open', o); h.classList.toggle('menu-open', o); panel.inert = !o; document.body.classList.toggle('lx-lock', o); if (o) { const a = panel.querySelector('a'); if (a) a.focus(); } };
    panel.inert = true;
    btn.addEventListener('click', () => set(btn.getAttribute('aria-expanded') !== 'true'));
    addEventListener('keydown', e => { if (e.key === 'Escape' && btn.getAttribute('aria-expanded') === 'true') { set(false); btn.focus(); } });
  }

  function init(root) { fragrance(root); pdp(root); rail(root); altImages(root); quickView(); words(root); reveal(root); tilt(root); if (hasGsap()) ScrollTrigger.refresh(); }
  header(); world(); init(document);
  addEventListener('load', () => { if (hasGsap()) ScrollTrigger.refresh(); });
  document.addEventListener('shopify:section:load', e => init(e.target));
})();
